import { useCallback, useState } from "react";

/**
 * DropZone — wrapper drag&drop riutilizzabile.
 * 
 * Props:
 *  - onFiles: (files: File[]) => void   // chiamato quando l'utente trascina/rilascia file
 *  - accept: string[]                    // array di mime accettati (es. ["application/pdf", "image/jpeg"])
 *  - multiple: boolean                   // permetti upload multipli (default true)
 *  - disabled: boolean
 *  - className: string                   // classi aggiuntive
 *  - children: ReactNode                 // contenuto interno (placeholder, pulsanti, lista file…)
 *  - testid: string                      // data-testid
 */
export function DropZone({
  onFiles,
  accept = [],
  multiple = true,
  disabled = false,
  className = "",
  children,
  testid = "dropzone",
}) {
  const [dragOver, setDragOver] = useState(false);

  const filterFiles = useCallback((files) => {
    const arr = Array.from(files || []);
    if (!accept || accept.length === 0) return arr;
    return arr.filter((f) =>
      accept.some((mime) => {
        if (mime.endsWith("/*")) return f.type.startsWith(mime.slice(0, -1));
        return f.type === mime;
      }),
    );
  }, [accept]);

  const handleDragEnter = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, [disabled]);

  const handleDragOver = useCallback((e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
    e.dataTransfer.dropEffect = "copy";
  }, [disabled, dragOver]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Solo se l'utente esce dal contenitore (non da un figlio)
    if (e.target === e.currentTarget) setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    const files = filterFiles(e.dataTransfer.files);
    if (files.length === 0) return;
    onFiles(multiple ? files : files.slice(0, 1));
  }, [disabled, filterFiles, multiple, onFiles]);

  return (
    <div
      data-testid={testid}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`relative transition-all ${
        dragOver
          ? "ring-2 ring-blue-500 ring-offset-2 bg-blue-50/60 border-blue-400"
          : ""
      } ${className}`}
    >
      {children}
      {dragOver && (
        <div
          data-testid={`${testid}-overlay`}
          className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-md flex items-center justify-center pointer-events-none z-10"
        >
          <span className="text-blue-700 font-bold text-sm bg-white/90 px-4 py-2 rounded-md shadow-sm">
            📎 Rilascia per caricare
          </span>
        </div>
      )}
    </div>
  );
}
